import {
  ControlAttestationRow,
  SelectOption,
  emptyFilledFilters,
  LookupValue
} from 'store/ducks/InternalControls/types';
import CustomSelect from 'components/CustomSelect';
import CustomFileUpload from 'components/CustomFileUpload';
import dayjs from 'dayjs';
import CustomDatePicker from 'components/CustomDatePicker';
import Helpers from 'utils/Helpers';
import {
  base_fields_control_test_attestation,
  instance_fields_control_test_attesta
} from './riskControlFieldsToDisable';
import * as Styled from '../pages/InternalControlsMain/RiskControl.styled';

import { ColumnsType } from 'antd/es/table';
import { Tooltip } from 'antd';

export const getControlAttestationColumns = (
  testingMethod: SelectOption[],
  controls: ControlAttestationRow[],
  designEffectiveness: SelectOption[],
  setControls: (controls: ControlAttestationRow[]) => void,
  trackingSetControls: (controls: ControlAttestationRow[]) => void,
  testType: SelectOption[],
  evidenceType: SelectOption[],
  testResult: SelectOption[],
  controlAttestationEffectiveness: SelectOption[],
  testStatus: SelectOption[],
  newControlIds?: Set<number>,
  isScopedView?: boolean,
  t?: (key: string) => string
): ColumnsType<ControlAttestationRow> => {
  const shouldDisable = (record: ControlAttestationRow, fieldKey: string) => {
    if (record.notUsed) return true;

    if (isScopedView) {
      return base_fields_control_test_attestation.has(fieldKey);
    }

    return instance_fields_control_test_attesta.has(fieldKey);
  };

  const update = (record: ControlAttestationRow, field: string, value: any) => {
    const newControls = [...controls];
    const idx = newControls.findIndex((c) => c.id === record.id);

    if (idx !== -1) {
      (newControls[idx] as any)[field] = value;
      trackingSetControls(newControls);
    }
  };

  const renderText = (
    value: any,
    record: ControlAttestationRow,
    field: string,
    placeholder: string,
    rows = 3
  ) => {
    const display =
      typeof value === 'object' && value !== null
        ? value?.name ?? ''
        : value ?? '';

    return (
      <>
        <Styled.GlobalTooltipStyles />
        <Tooltip title={display}>
          <Styled.ResizableTextArea
            value={display}
            rows={rows}
            disabled={shouldDisable(record, field)}
            placeholder={placeholder}
            onChange={(e) => update(record, field, e.target.value)}
          />
        </Tooltip>
      </>
    );
  };

  return [
    // ───────── CONTROL NAME ─────────
    {
      title: t ? t('CONTROL_NAME') : 'Control Name',
      dataIndex: 'controlName',
      key: 'controlName',
      width: 200,
      fixed: 'left',
      ellipsis: true,
      render: (value, record) =>
        renderText(value, record, 'controlName', 'Enter control name', 2)
    },

    // ───────── DESCRIPTION ─────────
    {
      title: t ? t('DESCRIPTION') : 'Description',
      dataIndex: 'controlDescription',
      key: 'controlDescription',
      width: 250,
      ellipsis: true,
      render: (value, record) =>
        renderText(value, record, 'controlDescription', 'Enter description', 3)
    },

    // ───────── TEST OBJECTIVE ─────────
    {
      title: t ? t('TEST_OBJECTIVE') : 'Test Objective',
      dataIndex: 'test_objectiveness_3',
      key: 'test_objectiveness_3',
      width: 220,
      render: (value, record) =>
        renderText(
          value,
          record,
          'test_objectiveness_3',
          'Enter test objective',
          3
        )
    },

    // ───────── TEST DESCRIPTION ─────────
    {
      title: t ? t('TEST_DESCRIPTION') : 'Test Description',
      dataIndex: 'test_description_3',
      key: 'test_description_3',
      width: 220,
      render: (value, record) =>
        renderText(
          value,
          record,
          'test_description_3',
          'Enter test description',
          3
        )
    },

    // ───────── TESTING METHOD ─────────
    {
      title: t ? t('TESTING_METHOD') : 'Testing Method',
      dataIndex: 'testingMethod',
      key: 'testingMethod',
      width: 200,
      render: (value, record) => (
        <CustomSelect
          value={value?.id ?? value ?? undefined}
          options={testingMethod}
          disabled={shouldDisable(record, 'testingMethod')}
          onChange={(id: number) => update(record, 'testingMethod', id)}
        />
      )
    },

    // ───────── EVIDENCE DESCRIPTION ─────────
    {
      title: t ? t('EVIDENCE_DESCRIPTION') : 'Evidence Description',
      dataIndex: 'evidence_description_3',
      key: 'evidence_description_3',
      width: 220,
      render: (value, record) =>
        renderText(
          value,
          record,
          'evidence_description_3',
          'Enter evidence description',
          3
        )
    },

    // ───────── EVIDENCE FILE ─────────
    {
      title: t ? t('EVIDENCE_FILE') : 'Evidence File',
      dataIndex: 'evidence_file_3',
      key: 'evidence_file_3',
      width: 200,
      render: (value, record) => {
        const isDisabled = shouldDisable(record, 'evidence_file_3');

        return (
          <CustomFileUpload
            defaultFileList={value ? [value] : []}
            onChange={(fileList) => {
              if (isDisabled) return;

              update(
                record,
                'evidence_file_3',
                fileList.length > 0 ? fileList[0].url || fileList[0].name : null
              );
            }}
          />
        );
      }
    },

    // ───────── PERFORMER ─────────
    {
      title: t ? t('PERFORMER_TOE') : 'Performer ToE',
      dataIndex: 'performer_toe',
      key: 'performer_toe',
      width: 200,
      render: (value, record) =>
        renderText(value, record, 'performer_toe', 'Enter performer', 2)
    },
    {
      title: t ? t('TEST_TYPE') : 'Test type',
      dataIndex: 'testType',
      key: 'testType',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlAttestationRow>('testType'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testType}
          placeholder='Select Test Type'
          disabled={shouldDisable(record, 'testType')}
          onChange={(id: number) => {
            const selected = testType.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testType = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    // ───────── APPROVER ─────────
    {
      title: t ? t('APPROVER') : 'Approver',
      dataIndex: 'approver',
      key: 'approver',
      width: 200,
      render: (value, record) =>
        renderText(value, record, 'approver', 'Enter approver', 2)
    },
    // ───────── DATES ─────────
    {
      title: t ? t('START_DATE') : 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      width: 150,
      render: (value, record) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'start_date')}
          onChange={(date: any) =>
            update(
              record,
              'start_date',
              date ? date.format('YYYY-MM-DD') : null
            )
          }
        />
      )
    },

    {
      title: t ? t('END_DATE') : 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 150,
      render: (value, record) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'end_date')}
          onChange={(date: any) =>
            update(record, 'end_date', date ? date.format('YYYY-MM-DD') : null)
          }
        />
      )
    },
    {
      title: t ? t('NEXT_TEST_DATE') : 'Next Test Date',
      dataIndex: 'next_test_date',
      key: 'next_test_date',
      width: 160,
      render: (value, record) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'next_test_date')}
          onChange={(date: any) =>
            update(
              record,
              'next_test_date',
              date ? date.format('YYYY-MM-DD') : null
            )
          }
        />
      )
    },

    // ───────── TEST STATUS ─────────
    {
      title: t ? t('TEST_STATUS') : 'Test Status',
      dataIndex: 'testStatus',
      key: 'testStatus',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter:
        Helpers.emptyFilledOnFilter<ControlAttestationRow>('testStatus'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testStatus}
          placeholder='Select Test Status'
          disabled={shouldDisable(record, 'testStatus')}
          onChange={(id: number) => {
            const selected = testStatus.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testStatus = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('TEST_RESULT') : 'Test Result',
      dataIndex: 'testResult',
      key: 'testResult',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter:
        Helpers.emptyFilledOnFilter<ControlAttestationRow>('testResult'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testResult}
          placeholder='Select Test Result'
          disabled={shouldDisable(record, 'testResult')}
          onChange={(id: number) => {
            const selected = testResult.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testResult = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    // ───────── EFFECTIVENESS ─────────
    {
      title: t
        ? t('CONTROL_ATTESTATION_EFFECTIVENESS')
        : 'Control Attestation Effectiveness',
      dataIndex: 'controlAttestationEffectiveness',
      key: 'controlAttestationEffectiveness',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlAttestationRow>(
        'controlAttestationEffectiveness'
      ),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlAttestationEffectiveness}
          placeholder='Select Control Attestation Effectiveness'
          disabled={shouldDisable(record, 'controlAttestationEffectiveness')}
          onChange={(id: number) => {
            const selected = controlAttestationEffectiveness.find(
              (o) => o.value === id
            );
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].controlAttestationEffectiveness = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('EVIDENCE_TYPE') : 'Evidence type',
      dataIndex: 'evidenceType',
      key: 'evidenceType',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter:
        Helpers.emptyFilledOnFilter<ControlAttestationRow>('evidenceType'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={evidenceType}
          placeholder='Select Evidence Type'
          disabled={shouldDisable(record, 'evidenceType')}
          onChange={(id: number) => {
            const selected = evidenceType.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].evidenceType = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    }
  ];
};
